import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ALLOWED_CONTENT_TYPES,
  MAX_IMAGES_PER_JOB,
  RECOMMENDED_IMAGE_COUNT,
  VAT_INCLUDED_NOTICE, PAYMENT_FAILED_IMAGE_NOTICE,
  countPrecut,
  cutoutPrice,
  jobFullNoticeMessage,
  uploadClosedNoticeMessage,
  precutSheetKeeps,
  precutSheetOpens,
  uploadItemLabel,
  uploadRejectMessage,
  uploadSubline,
  type PrecutSheetOutcome,
  type UploadItemState,
} from "@makit/shared";

import { ApiError, createJob, uploadImage } from "../../lib/api";
import { judgeFilesPrecut } from "../../lib/precut-file";
import { PrecutModal } from "./PrecutModal";
// ⛔ 이 판정을 이 파일 안으로 되돌리지 마라. 밖에 있는 이유는 그 파일 머리말에 있다 —
// 여기 있으면 `react` 때문에 CI 의 화면 시험이 부를 수 없어 **글자만 남는다.**
import { planUploadBatch, takenSlots } from "./plan-batch";
import { rememberJob } from "../../lib/job-store";
import { Button, CheckerThumb, StatusChip } from "../../components/primitives";
import { LimitBanner, PayNow } from "../../components/banners";

interface QueueItem {
  key: string;
  file: File;
  previewUrl: string;
  state: UploadItemState;
  /** 0~1. **`null` 은 「아직 한 번도 못 쟀다」이지 「0% 다」가 아니다**(PRD `7.5.9` ·
   * `7.6.6`). 브라우저 XHR 은 사실상 늘 진행률을 주므로 웹에서 `null` 이 오래 머무는 일은
   * 드물지만, **규칙을 플랫폼으로 가르지 않는다** — 갈리는 것은 잰 값이 있느냐다.
   * 앱에서 실제로 터진 자리는 `docs/UPLOAD_PROGRESS.md`. */
  progress: number | null;
  reason?: string;
  imageId?: string;
  /** 서버가 해시로 잡은 중복. 장이 늘지 않으므로 금액에서 뺀다. */
  duplicate?: boolean;
  /** 결제로 담기가 끝난 작업이라 거절된 장(`409 UPLOAD_CLOSED_AFTER_PAYMENT`). **다시 눌러도
   * 같다**(PRD `7.6.6` · D-94 ⓑ). 앱 `upload.tsx` 의 같은 이름 표지와 같은 뜻이다.
   * 사유 문장(`reason`)으로 가르지 않고 따로 둔다. 문장은 바뀔 수 있고 판단은 바뀌면 안 된다. */
  closed?: boolean;
  /** 이 행이 들어온 회차. `admit` 이 한 번에 넣은 묶음 하나가 한 회차다. 웹 패널은 떨어뜨린
   * 것이 계속 쌓여서, 회차를 적어 두지 않으면 「이번 회차」와 「이 패널 전체」가 갈리지 않는다. */
  round: number;
}

/** 허용 확장자는 shared 가 정본이다 — 화면 문구에 하드코딩하지 않는다. */
const ACCEPT = [...ALLOWED_CONTENT_TYPES].join(",");
const EXT_HINT = [...ALLOWED_CONTENT_TYPES].map((t) => t.replace("image/", "")).join(" · ");

export interface UploadPanelProps {
  /** W-10 은 W-20 보다 짧은 문구를 쓴다. */
  compact?: boolean;
  onJobReady?: (jobId: string) => void;
}

export function UploadPanel({ compact = false, onJobReady }: UploadPanelProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [over, setOver] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** `W-20b` 가 답을 기다리는 동안 들고 있는 것. **`null` 이면 모달이 안 떠 있다.**
   *
   *  ⭐ **묶음이 통째로 답을 받는다.** `7.6.5a` 의 "여기선 아직 못 합니다": **걸린 장만 빼고
   *  담기가 없다.** 전부 담거나 전부 다시 고르거나 둘이라, 걸린 장을 따로 들고 있을 자리도
   *  두지 않는다 — 두면 **화면이 못 하는 일을 자료구조가 할 수 있는 것처럼 보인다.** */
  const [pendingDrop, setPendingDrop] = useState<{ files: File[]; precut: number } | null>(null);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const running = useRef(false);

  const uploaded = items.filter((i) => i.state === "UPLOADED");
  // 중복은 서버가 장을 늘리지 않으므로 금액에서도 뺀다.
  const chargeable = uploaded.filter((i) => !i.duplicate);
  const busy = items.some((i) => i.state === "SENDING" || i.state === "QUEUED");
  /** ⓒ 판정은 **가장 최근 회차의 행만** 센다. 앱은 한 화면이 한 회차라 화면 전체를 세면 되지만,
   * 웹 패널은 여러 번 떨어뜨린 것이 한 목록에 쌓인다. 전체를 세면 「3장 올림 → 다른 탭에서 결제
   * → 2장 더 떨어뜨려 둘 다 거절」에서 앞 회차의 3장 때문에 줄이 서지 않는다. */
  const latestRound = items.reduce((max, i) => Math.max(max, i.round), -1);
  const roundItems = items.filter((i) => i.round === latestRound);
  const roundSettled = roundItems.every((i) => i.state !== "SENDING" && i.state !== "QUEUED");
  /** **회차가 끝났고 그 회차에서 한 장도 못 담았는데 담기 종료 거절이 하나라도 있으면** 목록 위에
   * 줄을 세운다(PRD `7.6.6` · `7.7.3a` · D-94 ⓒ). 앱 `upload.tsx` 의 `closedNotice` 와 같은 조건이다.
   * 판정은 화면이, 문장은 shared 가 맡는다. 그 회차에서 한 장이라도 올라갔으면 세우지 않는다.
   * ⛔ **「실패가 전부 담기 종료」로 좁히지 않는다.** 작업이 결제로 닫혔으면 다른 사유로 실패한
   * 장도 다시 올리면 결국 같은 거절을 받는다. 셀러가 알아야 할 것은 「새 작업」이다.
   *
   * ⭐ D-94 ⓑ(그 행에 `↻` 를 두지 않는다. `n장 다시 올리기` 에서 뺀다)는 웹에서 **이미 참이다.**
   * 이 패널에는 다시 올리기가 행별로도 일괄로도 없다. ⛔ 다시 올리기를 붙이는 날에는 `closed`
   * 행을 그 개수와 대상에서 빼야 한다(앱 `retriable`). */
  const closedNotice =
    roundItems.length > 0 &&
    roundSettled &&
    !roundItems.some((i) => i.state === "UPLOADED") &&
    roundItems.some((i) => i.state === "REJECTED" && i.closed)
      ? uploadClosedNoticeMessage()
      : null;

  const update = useCallback((key: string, patch: Partial<QueueItem>) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }, []);

  const pending = useRef<QueueItem[]>([]);
  const nextSortOrder = useRef(0);
  /** 다음 회차 번호. `admit` 이 부를 때마다 하나 오른다. */
  const nextRound = useRef(0);

  /** 순차 업로드다 — 병렬이면 진행률이 뭉개지고 실패한 장을 짚을 수 없다.
   *  2차 배치는 버리지 않고 이어붙인다. */
  const runQueue = useCallback(
    async (queued: QueueItem[]) => {
      pending.current = [...pending.current, ...queued];
      if (running.current) return;
      running.current = true;
      setError(null);
      try {
        let id = jobId;
        if (!id) {
          id = (await createJob()).jobId;
          setJobId(id);
          // 한 장이라도 올라가기 전에 기록한다 — 도중에 새로고침하면 돈이 될 사진의 작업을 잃는다.
          rememberJob(id, 0);
          onJobReady?.(id);
        }
        let done = 0;
        while (pending.current.length > 0) {
          const item = pending.current.shift();
          if (!item) break;
          const sortOrder = nextSortOrder.current++;
          // 보내기 시작한 순간에는 아직 아무것도 재지 않았다. `0` 이 아니라 `null` 이다.
          update(item.key, { state: "SENDING", progress: null });
          try {
            const res = await uploadImage({
              jobId: id,
              file: item.file,
              sortOrder,
              onProgress: (ratio) => update(item.key, { progress: ratio }),
            });
            // 서버가 해시로 중복을 잡으면 장이 늘지 않는다. 금액도 늘면 안 된다.
            update(item.key, {
              state: "UPLOADED",
              progress: 1,
              imageId: res.imageId,
              ...(res.duplicate ? { duplicate: true, reason: "이미 올린 사진이에요" } : {}),
            });
            if (res.duplicate) nextSortOrder.current--;
            else done += 1;
          } catch (err) {
            // ⛔ **`UPLOAD_CLOSED_AFTER_PAYMENT` 을 `올리지 못했어요` 로 떨어뜨리지 않는다.**
            //    그 꼬리는 **다시 누르면 되는 일**의 말이다(`7.5.9` 판정 13). 결제로 담기가
            //    끝난 작업은 **다시 눌러도 같고 사진을 빼도 소용없다.** 셀러가 할 일은
            //    **새 작업**이라, 꼬리를 갈라야 그 사실이 전해진다.
            const closed =
              err instanceof ApiError && err.code === "UPLOAD_CLOSED_AFTER_PAYMENT";
            const reason = closed
              ? uploadRejectMessage("UPLOAD_CLOSED")
              : err instanceof ApiError && err.status === 0
                ? "서버에 닿지 못했어요"
                : "올리지 못했어요";
            update(item.key, { state: "REJECTED", reason, closed });
            nextSortOrder.current--;
          }
          rememberJob(id, done);
        }
      } catch {
        setError("작업을 만들지 못했습니다. 잠시 뒤 다시 시도해 주세요.");
      } finally {
        running.current = false;
      }
    },
    [jobId, onJobReady, update],
  );

  /** ⭐ **떨어뜨린 것을 실제로 담는다.** `W-20b` 가 서기 전에는 이 일이 `accept` 였다.
   *
   *  **떼어 낸 이유는 모달이다.** 모달이 뜨면 담기는 **셀러의 답을 기다렸다가** 일어난다.
   *  ⛔ 이 함수를 드롭·파일 선택 핸들러에서 직접 부르지 마라 — 그러면 그 경로만 안 묻는다. */
  const admit = useCallback(
    (picked: File[]) => {
      // ⭐ **자리를 세는 기준은 「서버에 갈 행」이다.** 거절된 행은 서버에 가지 않으므로
      //    자리를 안 먹는다. ⛔ `items.length` 로 세면 형식이 안 맞아 빠진 장까지 자리를
      //    잡아먹어, HEIC 를 섞어 올린 셀러가 15장을 못 채운다.
      const taken = takenSlots(items.map((item) => item.state));
      const plan = planUploadBatch(
        picked.map((file) => ({ contentType: file.type, byteSize: file.size })),
        taken,
      );
      // 이 묶음 하나가 한 회차다. ⓒ 안내 줄은 가장 최근 회차만 센다(위 `closedNotice`).
      const round = nextRound.current++;
      const next: QueueItem[] = plan.verdicts.map((verdict) => {
        const file = picked[verdict.index]!;
        return {
          key: `${file.name}-${file.size}-${crypto.randomUUID()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          state: verdict.accepted ? "QUEUED" : "REJECTED",
          progress: null,
          round,
          ...(verdict.reason ? { reason: uploadRejectMessage(verdict.reason) } : {}),
        };
      });
      setItems((prev) => [...prev, ...next]);
      const queued = next.filter((i) => i.state === "QUEUED");
      // ⚠️ **빈 배열로 부르지 않는다.** `runQueue` 는 들어가자마자 `setError(null)` 로 줄을
      //    지운다 — 한 장도 못 받은 회차에서 부르면 **방금 세운 줄을 자기가 지운다.**
      if (queued.length > 0) void runQueue(queued);
      // **한 장도 못 받았고 그 이유가 자리뿐이면** 셀러가 한 동작을 했는데 화면에 진전이
      // 하나도 없다. 그 침묵만 받는 줄이다(PRD `7.5.9` 판정 3). 한 장이라도 받았으면
      // 받은 장이 스스로 진전을 말하므로 세우지 않는다.
      if (plan.accepted === 0 && plan.full > 0) setError(jobFullNoticeMessage());
    },
    [items, runQueue],
  );

  /** ⭐⭐ **담기 직전의 문 하나.** 드롭도 파일 선택도 전부 이 문을 지난다.
   *
   *  `7.6.5a` 가 시트의 자리를 **담는 순간 하나**로 잠갔고, `7.7.3` 이 **드롭존에 떨어뜨리는
   *  것이 웹의 담기**라고 적었다. 그래서 판정도 여기 하나다.
   *
   *  ⛔ **판정이 던져도 담기를 막지 않는다.** `judgeFilesPrecut` 이 던지지 않게 만들어져
   *  있지만(그 파일 머리말: *"여기서 던지지 않는다"*) 여기서도 받아 둔다 — **알리려다 담기를
   *  막는 것**이 `7.6.5a` 가 "막는 화면이 아니라 알리는 화면"이라고 적은 것의 정반대다. */
  const accept = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const picked = Array.from(files);
      let precut = 0;
      try {
        const assessments = await judgeFilesPrecut(picked);
        // ⛔ **뜨는 조건을 여기서 다시 세지 않는다.** `precutSheetOpens` 가 `countPrecut` 을
        //    보고, 그것이 `PRECUT` 만 센다 — `UNKNOWN` 은 **안 띄우는 쪽**이다.
        if (!precutSheetOpens(assessments)) {
          admit(picked);
          return;
        }
        precut = countPrecut(assessments);
      } catch {
        admit(picked);
        return;
      }
      setPendingDrop({ files: picked, precut });
    },
    [admit],
  );

  /** 모달이 답했다. **닫는 길 전부가 여기로 온다**(스크림 · `Esc` · `다시 고르기`). */
  const decide = useCallback(
    (outcome: PrecutSheetOutcome) => {
      const drop = pendingDrop;
      setPendingDrop(null);
      if (!drop) return;
      // ⛔ **물러나는 쪽은 아무것도 담지 않는다.** `outcome === "KEEP"` 을 여기서 쓰지 않는
      //    이유는 그 한 줄이 어떤 시험도 못 보는 자리이기 때문이다.
      if (!precutSheetKeeps(outcome)) return;
      admit(drop.files);
    },
    [admit, pendingDrop],
  );

  // ⭐ **배경 지우기를 시작하는 자리가 2026-09-25에 이 파일에서 나갔다.**
  //
  // 예전에는 여기서 `/process` 를 바로 불렀다. 그 주소가 이제 **승인된 결제를 요구하므로**
  // (`13.1` `O-13` (가). `lib/payment-gate.ts`) 여기서 부르면 언제나 402 다. 시작하는 자리는
  // 결제 화면(`W-26`)으로 옮겼고, **그 화면이 그 문을 지나는 유일한 길**이다.
  //
  // ⛔ **편의를 이유로 이 자리에 되살리지 마라.** 되살리면 셀러가 결제 화면을 지나지 않고
  //    누르는 길이 다시 생기고, 그 길의 끝은 「시작하지 못했습니다」 하나다.

  return (
    <div
      className={`dropzone${over ? " over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        // **판정이 붙으면서 비동기가 됐다**(파일 머리 4KB 를 읽고, 알파를 담을 수 있는
        // 형식이면 캔버스로 푼다). ⛔ `await` 없이 부르는 것이 맞다 — 드롭 핸들러가
        // 기다리면 **떨어뜨린 순간 브라우저가 멈춘 것처럼 보인다.**
        void accept(e.dataTransfer.files);
      }}
    >
      <span className="dz-ico" aria-hidden="true">
        ＋
      </span>
      <span>
        <b className="dz-h">
          {compact ? "사진을 끌어다 놓거나 파일에서 고르세요" : "찍거나, 끌어다 놓거나, 파일에서 고르세요"}
        </b>
        <span className="dz-b">
          {/* ⭐ **권장(8)과 상한(15)이 늘 함께 선다** (PRD `7.5.9` 판정 1·2. 2026-09-23).
              닿았을 때만 말하지 않는 이유: 웹 셀러는 폴더째 스무 장을 **한 동작으로** 끌어다
              놓아서, 뒤에 말하면 이미 실패한 뒤에 규칙을 배운다.
              ⛔ **`8~15장` 으로 뭉치지 마라.** 뭉치면 둘 다 조언으로 읽히고 16장째 거절이
              고장으로 보인다. **앞이 조언이고 뒤가 벽**이라는 것이 낱말로 갈려 보여야 한다. */}
          {EXT_HINT} · 상품 하나에 {RECOMMENDED_IMAGE_COUNT}장 권장 · 최대 {MAX_IMAGES_PER_JOB}장
          {compact ? null : (
            <>
              {" "}
              · <b>카메라가 없는 PC면</b> 파일 선택이나 <b>폰에서 이어서 하기</b>로 갑니다
            </>
          )}
        </span>
      </span>
      <span className="dz-actions">
        {compact ? null : (
          <Button
            label="카메라로 찍기"
            variant="ghost"
            small
            disabled
            disabledReason="PC 카메라로 촬영은 아직 정해지지 않았습니다. 기본 경로는 파일 선택입니다."
          />
        )}
        {/* ⛔ 버튼 옆 `미정` 배지를 걷었다 (2026-10-04 사용자 지시). 버튼이 왜 꺼졌는지는
            비활성 사유가 말한다. 버튼 자체는 업로드 흐름이라 그대로 둔다. */}
        <Button label="파일 선택" variant="ghost" small onClick={() => inputRef.current?.click()} />
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        onChange={(e) => {
          // ⚠️ **값을 비우기 전에 목록을 떠 둔다.** `accept` 가 비동기라, 먼저 비우면
          //    `FileList` 가 판정에 닿기 전에 사라진다.
          void accept(e.target.files);
          e.target.value = "";
        }}
      />

      {/* PRD `7.6.6` 은 이 줄의 자리를 **목록 위**로 적었다. 자리 축 줄(`error`)은 목록 아래에
          서 있지만 이번 범위가 아니라 옮기지 않는다. 생김새는 그 줄과 같은 `dz-reject` 다. */}
      {closedNotice ? <span className="dz-reject">{closedNotice}</span> : null}

      {items.length > 0 ? (
        <div className="uqueue">
          {items.map((item) => {
            const chip = uploadItemLabel(item.state);
            return (
              <div className="urow" key={item.key}>
                <CheckerThumb size={34} src={item.previewUrl} alt={item.file.name} />
                <span>
                  <span className="un mono">{item.file.name}</span>
                  <span className="us">
                    {uploadSubline(item.state, {
                      progress: item.progress,
                      ...(item.reason ? { reason: item.reason } : {}),
                    })}
                  </span>
                </span>
                <StatusChip tone={chip.tone} label={chip.label} />
              </div>
            );
          })}
        </div>
      ) : null}

      {error ? <span className="dz-reject">{error}</span> : null}

      {chargeable.length > 0 && jobId ? (
        <div className="uqueue">
          {/* 낱말은 용어 표(PRD 7.5.9)를 따른다 — 같은 성격의 돈을 화면마다 다르게 부르지 않는다. */}
          <PayNow label="지금 결제할 금액" amount={cutoutPrice(chargeable.length)} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap-reverse" }}>
            {/* ⭐ **2026-09-25: 여기서 바로 시작하지 않는다.** 서버가 승인된 결제를 요구하게
                됐고(`13.1` `O-13` (가)), 그래서 이 버튼이 부르던 주소는 이제 402 로 끊긴다.
                ⛔ 그대로 두면 셀러는 **누를 때마다 「시작하지 못했습니다」를 읽는다.**
                결제 화면(`W-26`)이 그 문을 지나는 유일한 길이라 그리로 보낸다. */}
            {/* 라벨은 `n장 결제` 다(PRD `7.5.1a`, 2026-10-17 `결제로` 에서 바꿈. 버튼 문구는
                명사로 끝난다). `n` 은 금액 줄과 같은 장수다 — 버튼과 금액이 다른 수를 세면
                누르기 전에 읽는 숫자가 둘이 된다. 앱 `A-22` 의 `n장 결제` 와 같은 꼴이다. */}
            <Button
              label={`${chargeable.length}장 결제`}
              onClick={() => navigate(`/jobs/${jobId}/pay`)}
              disabled={busy}
              style={{ flex: "1 1 auto", minWidth: 112 }}
            />
          </div>
          <LimitBanner icon="₩">
            {/* ⚠️ **옛 문구가 2026-09-25에 거짓이 됐다.** 결제는 붙었고 장당 청구가도 2026-09-13에
                확정됐고, 부가세 포함 여부도 2026-09-14 O-44 로 「부가세 포함」이 확정됐다. */}
            <b>결제가 끝나면 그 자리에서 시작합니다.</b> {PAYMENT_FAILED_IMAGE_NOTICE} {VAT_INCLUDED_NOTICE}
          </LimitBanner>
        </div>
      ) : null}

      {/* `W-20b` — **떨어뜨린 그 자리 위에 뜬다.** 셀러가 방금 한 동작의 결과 위여야 한다
          (`7.7.3`). ⛔ 결제 직전으로 옮기지 마라 — 거기엔 바꿀 길이 없다(`7.6.5a`). */}
      {pendingDrop ? <PrecutModal count={pendingDrop.precut} onDecide={decide} /> : null}
    </div>
  );
}
